import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getApiBaseUrl, getAuthRequestTimeoutMs } from "../../config/runtime";

interface User {
  id: number;
  name: string;
  email: string;
  image?: string;
  userType: string;
  authType: string;
}

interface AuthSessionPayload {
  success?: boolean;
  message?: string;
  user?: User | null;
}

type AuthSessionResult =
  | { kind: "authenticated"; user: User }
  | { kind: "guest" }
  | { kind: "timeout" }
  | { kind: "network_error" };

/**
 * Structured login error thrown by AuthContext.login().
 * Callers (Login.tsx) can inspect `code` to show the right UI.
 *
 * Codes:
 *   "unverified"      – account exists but email not verified
 *   "bad_credentials" – wrong email or password
 *   "server_error"    – unexpected server failure
 *   "network_error"   – could not reach the server at all
 */
export class LoginError extends Error {
  constructor(
    public readonly code: "unverified" | "bad_credentials" | "server_error" | "network_error",
    message: string
  ) {
    super(message);
    this.name = "LoginError";
  }
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  googleLogin: () => void;
  logout: () => Promise<void>;
  editIndex: number;
  setEditIndex: React.Dispatch<React.SetStateAction<number>>;
  textareaEdit: boolean;
  setTextareaEdit: React.Dispatch<React.SetStateAction<boolean>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_SESSION_CACHE_TTL_MS = 15_000;

let inFlightAuthRequest: Promise<AuthSessionResult> | null = null;
let authSessionCache: { checkedAt: number; result: AuthSessionResult } | null = null;

const readStoredUser = (): User | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const storedUser = window.localStorage.getItem("user");
  if (!storedUser) {
    return null;
  }

  try {
    return JSON.parse(storedUser) as User;
  } catch {
    clearStoredUser();
    return null;
  }
};

const cacheAuthSessionResult = (result: AuthSessionResult) => {
  if (result.kind === "authenticated" || result.kind === "guest") {
    authSessionCache = {
      checkedAt: Date.now(),
      result,
    };
  }
};

const clearAuthSessionCache = () => {
  authSessionCache = null;
  inFlightAuthRequest = null;
};

const clearStoredUser = () => {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem("user");
  }
};

const storeAuthenticatedUser = (user: User) => {
  if (typeof window !== "undefined") {
    window.localStorage.setItem("user", JSON.stringify(user));
  }
};

const checkServerSession = async (apiBaseUrl: string, signal: AbortSignal): Promise<AuthSessionResult> => {
  try {
    const response = await fetch(`${apiBaseUrl}/auth/login/success`, {
      credentials: "include",
      signal,
    });

    let payload: AuthSessionPayload | null = null;
    try {
      payload = (await response.json()) as AuthSessionPayload;
    } catch {
      payload = null;
    }

    if (response.ok && payload?.success && payload.user) {
      return { kind: "authenticated", user: payload.user };
    }

    // A missing session is a normal guest state, not a frontend error.
    // The backend may return either 200/success:false or legacy 401.
    if (response.status === 401 || payload?.success === false) {
      return { kind: "guest" };
    }

    return { kind: "network_error" };
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      return { kind: "timeout" };
    }

    console.warn("Auth session check failed. Continuing without blocking the UI.", error);
    return { kind: "network_error" };
  }
};

const getAuthSessionResult = (apiBaseUrl: string, signal: AbortSignal): Promise<AuthSessionResult> => {
  const cachedResult = authSessionCache;
  if (cachedResult && Date.now() - cachedResult.checkedAt < AUTH_SESSION_CACHE_TTL_MS) {
    return Promise.resolve(cachedResult.result);
  }

  if (inFlightAuthRequest) {
    return inFlightAuthRequest;
  }

  inFlightAuthRequest = checkServerSession(apiBaseUrl, signal)
    .then((result) => {
      cacheAuthSessionResult(result);
      return result;
    })
    .finally(() => {
      inFlightAuthRequest = null;
    });

  return inFlightAuthRequest;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [editIndex, setEditIndex] = useState<number>(0);
  const [textareaEdit, setTextareaEdit] = useState<boolean>(false);

  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  useEffect(() => {
    let isMounted = true;

    const restoreSession = async () => {
      const storedUser = readStoredUser();

      // Optimistically hydrate UI from localStorage so the navbar is not empty
      // on page load, but always validate against the server below.
      if (storedUser) {
        setUser(storedUser);
      }

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), getAuthRequestTimeoutMs());

      try {
        const result = await getAuthSessionResult(apiBaseUrl, controller.signal);

        if (!isMounted) {
          return;
        }

        if (result.kind === "authenticated") {
          setUser(result.user);
          storeAuthenticatedUser(result.user);
          return;
        }

        if (result.kind === "guest") {
          setUser(null);
          clearStoredUser();
          return;
        }

        // Timeout/network error: keep the optimistic localStorage state if it exists.
        // This avoids a jarring logout caused by a temporary connection issue.
      } finally {
        window.clearTimeout(timeoutId);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void restoreSession();

    return () => {
      isMounted = false;
    };
  }, [apiBaseUrl]);

  /**
   * Local login. Throws LoginError with a typed code on failure so callers
   * can show the right message (unverified vs wrong password vs server down).
   */
  const login = async (email: string, password: string): Promise<void> => {
    try {
      const res = await axios.post(
        `${apiBaseUrl}/auth/login-local`,
        { email, password },
        { withCredentials: true }
      );
      setUser(res.data.user);
      storeAuthenticatedUser(res.data.user);
      clearAuthSessionCache();
      cacheAuthSessionResult({ kind: "authenticated", user: res.data.user });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as { error?: string; message?: string } | undefined;
        const code = data?.error;
        const message = data?.message ?? "Login failed. Please try again.";

        if (code === "unverified") {
          throw new LoginError("unverified", message);
        }
        if (code === "bad_credentials") {
          throw new LoginError("bad_credentials", message);
        }
        if (code === "server_error") {
          throw new LoginError("server_error", message);
        }
        if (error.code === "ERR_NETWORK" || !error.response) {
          throw new LoginError("network_error", "Cannot reach the server. Please check your connection.");
        }
      }
      throw new LoginError("server_error", "Login failed. Please try again.");
    }
  };

  const googleLogin = () => {
    clearAuthSessionCache();
    const returnTo = typeof window !== "undefined" ? window.location.origin : "";
    const googleAuthUrl = `${apiBaseUrl}/auth/google?returnTo=${encodeURIComponent(returnTo)}`;
    window.open(googleAuthUrl, "_self");
  };

  const logout = async () => {
    try {
      await axios.post(
        `${apiBaseUrl}/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Logout request failed. Clearing local auth state anyway:", error);
    } finally {
      setUser(null);
      clearStoredUser();
      clearAuthSessionCache();
      cacheAuthSessionResult({ kind: "guest" });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        googleLogin,
        logout,
        editIndex,
        setEditIndex,
        textareaEdit,
        setTextareaEdit,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useGlobalContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
